import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Workflow,
  Cloud,
  Plug,
  Boxes,
  Cpu,
  FileText,
  Compass,
  ShieldCheck,
  Repeat,
  Building2,
  Network,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const TODAY_FACTS = [
  {
    icon: Boxes,
    title: 'WebCenter AP stack today',
    detail:
      'WebCenter Enterprise Capture for ingestion, Forms Recognition (WFR) for extraction, WebCenter Imaging for storage and search, SOA composites for routing, and ADF for the non-PO coding form — typically posting through the EBS Payables Open Interface.',
  },
  {
    icon: Cloud,
    title: 'ERP path is EBS to Fusion',
    detail:
      'The finance program of record is moving from Oracle E-Business Suite to Oracle Fusion Cloud ERP. That changes the system of record for suppliers, GL, AME approval routing, and the Payables import surface — from Payables Open Interface to Fusion FBDI and OIC REST.',
  },
  {
    icon: Compass,
    title: 'The natural question',
    detail:
      'Do we rebuild AP automation inside Fusion using Oracle\'s bundled invoice imaging, workflow, and AME — or move the AP automation layer to a modern external platform that integrates natively to Fusion Cloud Payables?',
  },
  {
    icon: ShieldCheck,
    title: 'What the AP team has built up',
    detail:
      'Years of multi-fund and multi-entity coding logic, complex approval routing, supplier-email exception handling, and audit-grade controls. That sophistication is the asset to protect through any AP platform move.',
  },
];

const LANDING_OPTIONS = [
  {
    icon: Cpu,
    title: 'Option 1 — Use Fusion native AP automation',
    detail:
      'Stand up Oracle\'s bundled invoice imaging, Intelligent Document Recognition, AP workflow, and AME inside Fusion Cloud ERP. Single-vendor stack, single contract, single support line. Coding and routing sophistication has to be re-implemented inside Fusion configuration and AME rules.',
  },
  {
    icon: Network,
    title: 'Option 2 — Modern external AP layer integrated to Fusion',
    detail:
      'Run AP automation on a modern AI-driven platform that integrates natively with Fusion Cloud Payables — FBDI batch load for posting invoices and OIC REST for real-time validation (supplier lookup, GL account validation, period close check, AME routing query). Fusion remains the system of record; the AP layer carries the sophistication forward.',
  },
];

const WHY_EXTERNAL = [
  {
    icon: Workflow,
    title: 'Multi-fund and multi-entity coding',
    detail:
      'A mature WebCenter implementation often codes invoices across multiple funds, legal entities, projects, and inter-company patterns in a single non-PO coding form. Reproducing that exact behaviour through Fusion native AP and AME usually requires significant configuration and ongoing maintenance.',
  },
  {
    icon: Repeat,
    title: 'Complex approval routing',
    detail:
      'Approval routing in established AP shops reflects years of policy edge cases — delegated authorities, tolerance bands, GL-coded routes, segregation of duties. A purpose-built AP layer keeps that routing logic visible and editable; native Fusion AME has to express it through a different framework.',
  },
  {
    icon: FileText,
    title: 'Supplier-email exception prevention',
    detail:
      'Supplier emails — duplicates, statements, queries, PDFs attached to other PDFs — are the unsolved part of AP. A dedicated AP layer can apply AI to triage the inbox and prevent exceptions before they reach Fusion, rather than after.',
  },
  {
    icon: Cpu,
    title: 'AI extraction without single-vendor lock-in',
    detail:
      'External AP layers can use multiple AI services for extraction — including OCI Document Understanding — and stay free to adopt better models as they appear. Native Fusion AP couples AP extraction to the Oracle AI roadmap.',
  },
];

const INTEGRATION_PATTERNS = [
  {
    step: '1',
    title: 'FBDI batch load — posting invoices to Fusion Payables',
    body:
      'Validated, approved invoices are written into Oracle\'s Fusion FBDI (File-Based Data Import) template for Payables and uploaded through the standard Fusion import process. FBDI is the Oracle-supported pattern for high-volume invoice posting to Fusion Cloud Payables — invoice header, distributions, and supporting attachments.',
  },
  {
    step: '2',
    title: 'OIC REST — real-time supplier lookup',
    body:
      'During capture and coding the external AP layer calls Oracle Integration Cloud REST endpoints to validate suppliers against the Fusion Supplier master. New suppliers and bank-detail changes route through the Fusion supplier workflow rather than being shadow-mastered in the AP layer.',
  },
  {
    step: '3',
    title: 'OIC REST — GL account and period validation',
    body:
      'Coded GL combinations are validated against the Fusion chart of accounts and the GL period status in real time. Invoices for closed periods are blocked at the AP layer rather than failing on FBDI import — the AP team sees the error inside their coding screen, not in a Fusion import log.',
  },
  {
    step: '4',
    title: 'OIC REST — AME routing query',
    body:
      'Where Fusion AME owns the routing rules, the AP layer calls Fusion AME via OIC REST to determine the next approver. Approvers can either approve inside the AP layer (with the result written back) or inside Fusion BPM Worklist — depending on the customer\'s preference for the approver experience.',
  },
  {
    step: '5',
    title: 'OIC REST — payment and status writebacks',
    body:
      'Once Fusion Payables runs payment, status (paid, cancelled, on-hold) flows back to the AP layer through OIC REST so that AP and supplier portals show the same truth. The AP layer never duplicates payment processing — that stays in Fusion.',
  },
];

const SURVIVES = [
  {
    icon: Workflow,
    title: 'Coding sophistication',
    detail:
      'The multi-fund, multi-entity, project-aware non-PO coding logic the AP team has built in the ADF coding form translates forward into the AP layer\'s coding screen — including the validation rules and the autocomplete behaviour the team relies on.',
  },
  {
    icon: Repeat,
    title: 'Approval routing',
    detail:
      'Existing approval matrices, delegation rules, and tolerance bands are re-expressed in the AP layer\'s workflow or kept in Fusion AME and queried via OIC REST. Either way, the routes the AP team understands stay intact.',
  },
  {
    icon: ShieldCheck,
    title: 'Audit trail',
    detail:
      'Every action — capture, extraction, coding, approval, posting, status — is captured in the AP layer\'s audit log to the same depth that WebCenter Imaging recorded it. Audit teams keep the per-invoice timeline they\'ve built reporting on.',
  },
  {
    icon: Building2,
    title: 'Supplier master integration',
    detail:
      'Suppliers stay mastered in Fusion. The AP layer reads them through OIC REST and surfaces them in the coding screen exactly as the AP team is used to seeing them — same names, same site logic, same default coding.',
  },
];

const WHAT_CHANGES = [
  {
    icon: Cloud,
    title: 'WebCenter is no longer the data store',
    detail:
      'WebCenter Content / Imaging stops being the production AP repository. The modern AP layer\'s repository — typically OCI Object Storage for invoice images, with database-backed metadata — becomes the system of record for invoice content. WebCenter parity audit trail is preserved.',
  },
  {
    icon: Plug,
    title: 'Posting surface changes from Payables Open Interface to FBDI',
    detail:
      'The integration pattern moves from the EBS Payables Open Interface (PL/SQL inserts into AP_INVOICES_INTERFACE) to the Fusion FBDI template + import process. This is the same change every EBS-to-Fusion AP migration faces, and it\'s solved by Oracle-supported tooling.',
  },
  {
    icon: Network,
    title: 'Real-time validation moves to OIC REST',
    detail:
      'Validation calls that used to hit EBS via SOA or direct DB look-ups move to OIC REST against the Fusion endpoints. The AP layer takes care of the wiring so the AP team\'s experience stays the same — same screen, same checks, same response time.',
  },
  {
    icon: FileText,
    title: 'Capture and extraction are no longer WFR',
    detail:
      'Forms Recognition templates and learned data don\'t carry forward. Extraction is re-implemented on the modern AP layer\'s AI extraction stack — typically with a parallel-run validation period so the AP team can confirm field-level accuracy before cutting over.',
  },
];

const SCENARIOS = [
  {
    icon: Cloud,
    title: 'EBS-to-Fusion migration in flight, WebCenter complexity blocking',
    detail:
      'The Fusion program is moving and the question of what to do with WebCenter AP keeps coming up. Migrating the AP layer to a modern external platform de-risks the Fusion cutover by separating the AP automation question from the ERP migration question.',
  },
  {
    icon: Workflow,
    title: 'Fusion native AP doesn\'t handle the multi-entity coding pattern',
    detail:
      'The existing WebCenter implementation codes invoices across funds, entities, and projects in a way that Fusion native AP and AME can\'t express cleanly. An external AP layer keeps the coding logic the AP team relies on while writing the right output to Fusion via FBDI.',
  },
  {
    icon: Building2,
    title: 'Reducing the Oracle Partner / consultant dependency footprint',
    detail:
      'Every small change to the WebCenter implementation is a development project that needs an Oracle Partner or specialist consultant. The AP team wants a platform where routine changes — new coders, new approval thresholds, new field mappings — are configuration not code.',
  },
  {
    icon: Cpu,
    title: 'AI extraction without an OCI Document Understanding-only commitment',
    detail:
      'The AP team wants the AI extraction win that\'s now possible but doesn\'t want to bet AP automation on a single vendor\'s extraction roadmap. An external AP layer keeps options open across multiple AI services, including OCI Document Understanding.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    tier: 'Tier 1 · Cross-product migration assessment',
    title: 'Fixed-fee read of your WebCenter AP + Fusion target state',
    detail:
      'Andrew reads the existing WebCenter AP implementation directly — Capture, WFR, Imaging, SOA, ADF coding form, EBS Payables Open Interface — and maps it against the Fusion Cloud ERP target. Output: a documented migration plan covering FBDI design, OIC REST endpoint inventory, AME routing decisions, and supplier master integration. Plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    tier: 'Tier 2 · Architecture review',
    title: 'One-week deep read of the running implementation',
    detail:
      'A one-week fixed-fee read of your live WebCenter AP — Forms Recognition rules, SOA composites, ADF non-PO coding form, EBS Payables Open Interface mappings, supplier and GL integration. Output: structured inventory used as input to the Fusion AP landing decision (Option 1 native, Option 2 external layer).',
  },
  {
    tier: 'Tier 3 · Standard cross-product migration consulting',
    title: 'Full delivery: WebCenter AP to Fusion AP on EZ Cloud',
    detail:
      'EZ Cloud delivers the AP layer migration end-to-end — extraction model training, coding screen build, FBDI design, OIC REST integration, AME routing decisions, parallel run, cutover. Designed to run alongside the EBS-to-Fusion ERP program without blocking it. Engagement pricing established per situation.',
  },
  {
    tier: 'Tier 4 · Forward Deployed Engineering',
    title: 'Founder-led engineering for heavy WebCenter customization archeology',
    detail:
      'For implementations where WebCenter was customized heavily over many years — bespoke SOA composites, deep ADF coding form changes, custom FIPSA accelerators, undocumented integration patterns — FDE puts Andrew directly inside the customer\'s engineering loop. Reads the implementation, recreates the behaviour on EZ Cloud + Fusion, and stays embedded through cutover.',
  },
];

const FAQS = [
  {
    q: 'Why not just use Fusion native AP automation?',
    a: 'Fusion native AP automation is a real option that customers with a mature WebCenter AP implementation often pass on because it doesn\'t yet match the coding sophistication, approval routing flexibility, and supplier-email exception handling the AP team has built over years on WebCenter. Fusion native AP is improving, and for shops close to out-of-the-box it can be the more direct path. The right answer depends on how much of that sophistication is real working policy versus historical artefact — which is what the migration assessment is designed to surface.',
  },
  {
    q: 'FBDI vs OIC REST — when do you use each?',
    a: 'FBDI handles batch posting and OIC REST handles real-time validation and writebacks — both are Oracle-supported integration patterns. Invoice headers, distributions, and supporting attachments are written into the Oracle FBDI template for Payables and uploaded via the Fusion import process. OIC REST covers everything that needs to happen in real time — supplier lookup, GL account and period validation, AME routing query, payment status writeback.',
  },
  {
    q: 'Does this preserve our AME routing?',
    a: 'Yes — AME routing is preserved through one of two patterns. Pattern A keeps Fusion AME as the routing engine and the AP layer calls Fusion AME via OIC REST to ask "who approves this invoice next?" Pattern B re-expresses the AME rules inside the AP layer\'s workflow so approvers can stay in the AP layer\'s screen and approval results are written back to Fusion. The choice depends on whether the AP team wants the Fusion BPM Worklist experience for approvers or the AP layer\'s experience.',
  },
  {
    q: 'How does supplier master integration work?',
    a: 'Suppliers stay mastered in Fusion Cloud ERP and the AP layer reads them through OIC REST. The AP layer surfaces suppliers in the coding screen exactly as the AP team is used to seeing them — including site logic and default coding. New supplier requests and bank-detail changes route through the standard Fusion supplier workflow rather than being shadow-mastered in the AP layer, so audit and segregation-of-duties controls stay where Finance expects them.',
  },
  {
    q: 'When should this happen relative to the EBS-to-Fusion migration?',
    a: 'Sequencing depends on program timeline, Fusion go-live date, and AP team capacity — there\'s no single right answer. Some customers move AP off WebCenter to the external layer first (still posting to EBS via the Payables Open Interface) and switch the posting surface to Fusion FBDI when the ERP cutover happens, which de-risks the Fusion cutover by separating the two changes. Other customers wait until Fusion is live and then migrate AP. The assessment surfaces which sequencing fits your specific situation.',
  },
  {
    q: 'What\'s the typical timeline?',
    a: 'Cross-product migrations typically run 4-9 months depending on the depth of WebCenter customization, the number of legal entities, and whether the migration runs in parallel with the Fusion ERP program. Implementations with extensive Forms Recognition template libraries, sophisticated SOA approval composites, or heavy ADF non-PO coding form customizations land at the longer end. The assessment phase typically runs 2-4 weeks and surfaces the realistic timeline for your specific implementation.',
  },
  {
    q: 'How does the cost compare to rebuilding AP automation inside Fusion?',
    a: 'Cost comparison depends on the depth of the existing WebCenter implementation, and the assessment outputs a direct comparison for your situation. For customers with extensive coding logic, complex routing, and supplier-email exception handling already in place on WebCenter, rebuilding all of that inside Fusion native AP configuration and AME usually costs more than migrating to an external AP layer — and produces a result that\'s harder to change later. For customers whose WebCenter implementation is close to out-of-the-box, Fusion native AP can be the more direct path.',
  },
  {
    q: 'What\'s the Oracle Reference Architecture status of this pattern?',
    a: 'The FBDI + OIC REST integration pattern is the Oracle-supported way to integrate an external AP layer with Fusion Cloud Payables, and Oracle documents both interfaces directly. EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle technical case studies. This isn\'t a workaround pattern; it\'s the documented integration path.',
  },
];

const WebCenterToFusionApMigration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const schemaAbout = [
    'Oracle Fusion Cloud Payables',
    'Oracle Fusion Cloud ERP',
    'WebCenter to Fusion Migration',
    'FBDI',
    'OIC REST',
    'Oracle Integration Cloud',
    'Accounts Payable Automation',
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'WebCenter to Oracle Fusion Cloud AP — Migrate Off WebCenter Without Losing AP Sophistication | EZ Cloud',
    description:
      'Many WebCenter AP customers are also moving from Oracle EBS to Oracle Fusion Cloud ERP. The natural question: do we rebuild AP automation inside Fusion, or move the AP layer to a modern external platform? EZ Cloud preserves the AP sophistication of your WebCenter implementation while integrating natively to Oracle Fusion Cloud Payables via FBDI and OIC REST.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-to-fusion-ap-migration',
    },
    about: schemaAbout.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WebCenter to Oracle Fusion Cloud AP — Migrate Off WebCenter Without Losing AP Sophistication | EZ Cloud"
        description="Many WebCenter AP customers are also moving from Oracle EBS to Oracle Fusion Cloud ERP. The natural question: do we rebuild AP automation inside Fusion, or move the AP layer to a modern external platform? EZ Cloud preserves the AP sophistication of your WebCenter implementation while integrating natively to Oracle Fusion Cloud Payables via FBDI and OIC REST."
        canonical="https://ezcloud.ai/solutions/webcenter-to-fusion-ap-migration"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter to Fusion AP Migration',
            url: 'https://ezcloud.ai/solutions/webcenter-to-fusion-ap-migration',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Compass className="w-4 h-4" aria-hidden />
              WebCenter → Fusion AP · Cross-product migration
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              WebCenter to Oracle Fusion Cloud AP migration
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                The natural next step after the EBS-to-Fusion migration.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Move AP off WebCenter to a modern automation layer that integrates natively with
              Fusion Cloud Payables. Same coding sophistication, AI-driven processing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-fusion-migration-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Fusion AP Migration Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter AP customers are also on the EBS-to-Fusion path
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Many WebCenter AP customers are simultaneously migrating from Oracle E-Business
                  Suite to Oracle Fusion Cloud ERP.
                </strong>{' '}
                That moves the system of record for suppliers, GL, and approval routing — and it
                changes the integration surface for AP from the EBS Payables Open Interface to
                Fusion FBDI and OIC REST.
              </p>
              <p>
                The AP layer can move off WebCenter to a modern platform that integrates natively
                with Fusion via{' '}
                <strong className="text-gray-900">FBDI batch load for posting</strong> and{' '}
                <strong className="text-gray-900">
                  OIC REST for real-time validation
                </strong>{' '}
                (supplier lookup, GL account validation, period close check, AME routing query).
                Both are Oracle-supported integration patterns.
              </p>
              <p>
                The result: the coding sophistication, approval routing, audit trail, and supplier
                master integration the AP team has built up on WebCenter survive the migration —
                while WebCenter itself stops being the AP automation platform. See the related{' '}
                <Link
                  to="/solutions/webcenter-12c-end-of-support-decision"
                  className="text-primary hover:text-accent font-semibold"
                >
                  December 2026 12c support decision
                </Link>{' '}
                and the broader{' '}
                <Link
                  to="/solutions/modernize-oracle-webcenter"
                  className="text-primary hover:text-accent font-semibold"
                >
                  modernization framework
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Where these customers are today */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where these customers are today
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The shape of the stack and the program
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The customers asking this question share a stack pattern and a program pattern. Both
              shape the decision.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {TODAY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Two ways to land AP on Fusion */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Two ways to land AP on Fusion
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Fusion native AP vs. modern external AP layer
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Both are legitimate paths. The choice is shaped by how much AP sophistication is
              already in place on WebCenter and how much of it is real working policy versus
              historical artefact.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {LANDING_OPTIONS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why customers often pick the external layer */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Why customers often pick the external layer
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The AP sophistication gap
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Fusion native AP automation is improving but doesn't yet match the AP sophistication
              of a mature WebCenter implementation. An external AP layer fills the gap without
              requiring AP to step backwards on what they've built.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_EXTERNAL.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Integration architecture */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Integration architecture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              FBDI for posting, OIC REST for real-time validation
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Oracle-supported pattern for integrating an external AP layer with Fusion Cloud
              Payables. FBDI is the batch posting surface; OIC REST is the real-time
              validation and writeback surface.
            </p>
            <div className="space-y-5">
              {INTEGRATION_PATTERNS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/integrations/oracle"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full Oracle integration surface
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* What survives the migration */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What survives the migration
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The AP sophistication carries forward
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The reason to keep an external AP layer is to protect what the AP team has built —
              not to replace it. Four things specifically survive the move.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SURVIVES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What changes */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What changes
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where the architecture actually shifts
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The non-trivial changes are the data store, the posting surface, the real-time
              validation surface, and the extraction stack. Each is straightforward in isolation;
              the value of an experienced delivery partner is sequencing them safely.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHAT_CHANGES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              The modern AP layer&apos;s storage tier is typically OCI Object Storage with
              WebCenter-parity audit trail — same retention, same searchability, same per-invoice
              timeline the audit team is used to.
            </p>
          </div>
        </section>

        {/* Common scenarios we hear */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four patterns customers describe
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the framings AP and Finance leaders use when they describe their situation.
              None of them is a vendor critique — they&apos;re descriptions of where the program is and
              what they&apos;re trying to keep working through it.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-3xl mx-auto">
              For healthcare AP teams running the same pattern see the{' '}
              <Link
                to="/solutions/healthcare-oracle-fusion"
                className="text-primary hover:text-accent font-semibold"
              >
                Healthcare on Oracle Fusion
              </Link>{' '}
              page, and for the broader native-ERP integration story see{' '}
              <Link
                to="/technology/native-erp"
                className="text-primary hover:text-accent font-semibold"
              >
                Native ERP integration
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WebCenter → Fusion AP migrations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for cross-product migration
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard cross-product migration consulting is the default delivery mode. Forward
              Deployed Engineering is the right pick when the WebCenter implementation has years of
              undocumented customization that needs founder-level archeology to safely carry
              forward.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.tier} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.tier}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.detail}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths — including the WebCenter to Fusion AP
              migration path — with stack-specific considerations, eight diagnostic questions, and a
              vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the WebCenter → Fusion AP migration
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your WebCenter to Fusion AP migration
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years specifically on Oracle WebCenter — Content (UCM),
              Imaging (IPM), Capture (ODC), Forms Recognition (WFR), SOA Suite, WebLogic, ADF. Walk
              through your stack and the Fusion AP landing decision in the context of your specific
              implementation and program timeline.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-fusion-migration-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Fusion AP Migration Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterToFusionApMigration;
